import { inject, Injectable, Injector } from '@angular/core';
import { getApp } from '@angular/fire/app';
import { getStorage, ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, tap } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { StorageService } from 'src/app/storage.service';
import { ChunkService, CreateChunkResponse } from 'src/app/views/content/chunk/chunk.service';
import { ContentUtilsService } from 'src/app/views/content/content-utils.service';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';

import { ContentStateService } from 'src/app/views/content/content-state.service';
import { v4 as uuidv4 } from 'uuid';

export interface UploadParams {
  contentID?: string;
  courseID?: string;
  blogID?: string;
  lecture?: any;
  section?: any;
  noteID?: string;
  contentName?: string;
  contentType?: string;
  insertBelowID?: string;
  insertAsFirst?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private chunkService = inject(ChunkService);
  private authService = inject(AuthService);
  private env = inject(EnvService);
  private storage = inject(Storage);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private contentUtilsService = inject(ContentUtilsService);
  private storageService = inject(StorageService);
  private tenantService = inject(TenantService);

  private injector = inject(Injector);

  private get contentStateService(): ContentStateService {
    return this.injector.get(ContentStateService);
  }

  private hasVideoTrack(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      if (!file.type.startsWith('video/')) {
        return resolve(false);
      }
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      
      const objectUrl = URL.createObjectURL(file);
      
      const cleanUp = () => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch (e) {}
        video.onloadedmetadata = null;
        video.onerror = null;
      };

      video.onloadedmetadata = () => {
        const hasVideo = video.videoWidth > 0 && video.videoHeight > 0;
        cleanUp();
        resolve(hasVideo);
      };
      
      video.onerror = () => {
        cleanUp();
        resolve(false);
      };

      video.src = objectUrl;
    });
  }

  private getMediaDuration(file: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
      if (file.type.startsWith('image')) {
        return resolve(0);
      }
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = (e) => {
        reject('Error loading video metadata');
      };
      video.src = URL.createObjectURL(file);
    });
  }

  private async uploadToStorage(file: File, filename: string, bucket?: string, includeAppNameInPath: boolean = true): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.authService.getValidToken();

        let storage;
        if (bucket) {
          const app = getApp();
          storage = getStorage(app, bucket);
        } else {
          storage = this.storage;
        }

        const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
        const path = includeAppNameInPath ? `${activeTenant}/${filename}` : filename;

        const storageRef = ref(storage, path);
        const snapshot = await uploadBytesResumable(storageRef, file);
        resolve(snapshot);
      } catch (err) {
        this.toast.snackbar.open(this.translate.instant('Snackbar.FileUploadFail'), 'Ok', { duration: 3000 });
        reject(err);
      }
    });
  }

  uploadFile(file: Blob, type: 'video' | 'image', params: UploadParams): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const duration = await this.getMediaDuration(file);
        const extension = file.type.split('/')[1] || 'webm';
        const filename = `${uuidv4()}.${extension}`;
        const path = `${type}/${filename}`;
        const fileToUpload = new File([file], filename, { type: file.type });

        await this.uploadToStorage(fileToUpload, path);

        const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
        const fileLink = `${this.env.linkPrefix}${activeTenant}/${path}`;
        const chunkParams = {
          contentID: this.contentStateService.contentId,
          courseID: this.contentStateService.courseId,
          noteID: this.contentStateService.noteId,
          blogID: this.contentStateService.blogId,
          section: this.contentStateService.section,
          lecture: this.contentStateService.lecture,
          contentName: this.contentStateService.contentName,
          contentType: this.contentStateService.contentType,
          ...params,
          fileLink,
          type,
          duration,
          origin: 'create-chunk',
          language: this.translate.currentLang || 'fr',
        };

        this.chunkService
          .createUploadedChunk(chunkParams)
          .pipe(
            tap(async (res: CreateChunkResponse) => {
              const { chunk, content } = res;
              await this.storageService.store('chunks', chunk);
              const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
              this.storageService.store('contents', composedContent);
            }),
            catchError((err) => {
              console.error('Error creating chunk:', err);
              return [];
            })
          )
          .subscribe({
            next: (res) => resolve(res),
            error: (err) => reject(err),
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  uploadFileWithProgress(file: File, params: UploadParams): Observable<number> {
    return new Observable((observer) => {
      const runUpload = async () => {
        try {
          const extension = file.name.split('.').pop() || '';
          const filename = `${uuidv4()}.${extension}`;

          let type: string;
          if (file.type.startsWith('image')) {
            type = 'image';
          } else if (file.type.startsWith('video')) {
            const hasVideo = await this.hasVideoTrack(file);
            type = hasVideo ? 'video' : 'audio';
          } else if (file.type.startsWith('audio')) {
            type = 'audio';
          } else {
            type = 'file';
          }

          const path = `${type}/${filename}`;

          const uploadTask = this.uploadFileAndGetProgress(file, path);

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              observer.next(Math.round(progress));
            },
            (error) => {
              observer.error(error);
            },
            async () => {
              const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
              const fileLink = `${this.env.linkPrefix}${activeTenant}/${path}`;
              // const duration = await this.getMediaDuration(file);

              const chunkParams = {
                contentID: this.contentStateService.contentId,
                courseID: this.contentStateService.courseId,
                noteID: this.contentStateService.noteId,
                blogID: this.contentStateService.blogId,
                section: this.contentStateService.section,
                lecture: this.contentStateService.lecture,
                contentName: this.contentStateService.contentName,
                contentType: this.contentStateService.contentType,
                ...params,
                fileLink,
                type,
                origin: 'create-chunk',
                filename: file.name,
                // duration,
              };

              this.chunkService
                .createUploadedChunk(chunkParams)
                .pipe(
                  tap(async (res: CreateChunkResponse) => {
                    const { chunk, content } = res;
                    await this.storageService.store('chunks', chunk);
                    const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
                    this.storageService.store('contents', composedContent);
                  }),
                  catchError((err) => {
                    console.error('Error creating chunk:', err);
                    observer.error(err);
                    return [];
                  })
                )
                .subscribe({
                  next: () => observer.complete(),
                  error: (err) => observer.error(err),
                });
            }
          );
        } catch (err) {
          observer.error(err);
        }
      };

      runUpload();
    });
  }

  uploadFileAndGetProgress(file: File, filename: string) {
    this.authService.getValidToken();

    const storage = this.storage;
    const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
    const storagePath = `${activeTenant}/${filename}`;
    const storageRef = ref(storage, storagePath);

    return uploadBytesResumable(storageRef, file);
  }

  uploadFileAndGetProgressWitoutAppFolderPrefix(file: File, filename: string) {
    this.authService.getValidToken();

    const storage = this.storage;
    const storagePath = `${filename}`;
    const storageRef = ref(storage, storagePath);

    return uploadBytesResumable(storageRef, file);
  }
}
